<script setup lang="ts">
import { MISSION_HEROICONS } from './missionIcons'
import { renderMissionText } from './missionText'

import type { WebappMission } from '@/types/types'

const { user } = useLocalStorage()
const { getContrast } = useColor()

const DEFAULT_BACKGROUND = '#000000'

const props = withDefaults(
  defineProps<{
    mission: WebappMission
    color?: string
    pointsLabel?: string
    locale: string
  }>(),
  {
    color: '#000000',
    pointsLabel: 'pts'
  }
)

const backgroundColor = computed(() => {
  const hex = (props.color ?? '').trim().replace(/^#/, '')

  return /^[0-9a-f]{6}$/i.test(hex) ? `#${hex}` : DEFAULT_BACKGROUND
})
const isLight = computed(() => getContrast(backgroundColor.value) !== '#FFFFFF')

const veil = computed(() => (isLight.value ? 'bg-black/5' : 'bg-white/10'))
const veilBorder = computed(() => (isLight.value ? 'border-black/5' : 'border-white/10'))
const cardBorder = computed(() => (isLight.value ? 'border-black/[0.08]' : 'border-white/[0.08]'))
const titleColor = computed(() => (isLight.value ? 'text-foreground' : 'text-white'))
const subtitleColor = computed(() => (isLight.value ? 'text-muted-foreground' : 'text-white/80'))

const heroicon = computed(() =>
  props.mission.iconType === 'heroicon'
    ? (MISSION_HEROICONS[props.mission.iconValue] ?? MISSION_HEROICONS.SparklesIcon)
    : null
)

const translated = (field: 'title' | 'subtitle') =>
  renderMissionText(props.mission[field], props.locale, {
    points: props.mission.points,
    label: props.pointsLabel,
    merchantName: user.value.merchant.name ?? ''
  })

const title = computed(() => translated('title'))
const subtitle = computed(() => translated('subtitle'))
</script>

<template>
  <div class="w-full rounded-xl px-4 py-6" :style="{ backgroundColor }">
    <div
      class="mx-auto flex w-[350px] max-w-full flex-col gap-[15px] overflow-hidden rounded-3xl border p-5"
      :class="[veil, cardBorder]"
    >
      <div class="flex items-start gap-[15px]">
        <div class="flex size-10 shrink-0 items-center justify-center rounded-xl border-2" :class="[veil, veilBorder]">
          <component :is="heroicon" v-if="heroicon" class="size-5" :class="titleColor" aria-hidden="true" />
          <span v-else class="text-xl leading-none" aria-hidden="true">{{ mission.iconValue }}</span>
        </div>

        <span
          v-if="mission.points > 0"
          class="ml-auto flex h-6 shrink-0 items-center rounded-full px-2 text-sm font-semibold leading-4"
          :class="[veil, titleColor]"
        >
          {{ mission.points }} {{ pointsLabel }}
        </span>
      </div>

      <div class="flex flex-col gap-2.5">
        <p class="text-sm font-bold leading-[18px]" :class="titleColor">{{ title }}</p>
        <p v-if="subtitle" class="text-sm font-medium leading-[18px]" :class="subtitleColor">{{ subtitle }}</p>
      </div>
    </div>
  </div>
</template>
